import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { fetchBatchGroups, fetchGroupCalendar } from '../../api/adminApi';
import MonthGrid from './calendar/MonthGrid';
import DayDetail from './calendar/DayDetail';

// The calendar picks a batch group the Month Planner also uses ("Class 8 · CBSE ·
// Stream 1") and shows every batch in that group at once — no per-batch filter.
const TRACK_LABELS = {
  foundation: 'Foundation',
  jee: 'JEE',
  smartplus: 'Smart Plus',
  other: 'Other',
};

// The picker iterates this, so a track missing from it never appears as an
// option. Keep in step with the tracks `util/batchGrouping.js` emits.
const TRACK_ORDER = ['foundation', 'jee', 'smartplus', 'other'];

// Mode filter chips — matches Schedule.mode values in the data. `Offline` is a
// class taught in person, which the Ideal School batches run from June onward.
const MODES = ['Live', 'Offline', 'Unit Test', 'Exam', 'Recorded'];

// Current IST month as 'YYYY-MM'
function currentMonth() {
  const now = new Date();
  const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000);
  return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const CalendarSection = () => {
  const [groups, setGroups] = useState([]);
  const [groupKey, setGroupKey] = useState('');
  const [month, setMonth] = useState(currentMonth());
  const [modes, setModes] = useState([]); // active mode filters; empty = all
  const [data, setData] = useState(null); // { summary, days }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const toggleMode = (m) =>
    setModes((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  // Load the batch groups once; the calendar then loads every batch in the group.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetchBatchGroups(false);
        if (!active) return;
        const loadedGroups = res.groups || [];
        setGroups(loadedGroups);
        setGroupKey((prev) => (
          loadedGroups.some((candidate) => candidate.key === prev)
            ? prev
            : (loadedGroups[0] ? loadedGroups[0].key : '')
        ));
      } catch (loadError) {
        if (active) setError('Failed to load batch groups.');
      }
    })();
    return () => { active = false; };
  }, []);

  const selectedGroup = useMemo(
    () => groups.find((candidate) => candidate.key === groupKey) || null,
    [groups, groupKey],
  );

  const batchIds = useMemo(
    () => (selectedGroup ? selectedGroup.batches.map((batch) => batch.id) : []),
    [selectedGroup],
  );

  const loadCalendar = useCallback(async () => {
    if (batchIds.length === 0) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGroupCalendar(batchIds, month, modes);
      setData(res);
    } catch (e) {
      setError('Failed to load calendar.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [batchIds, month, modes]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const [y, m] = month.split('-').map(Number);
  const s = data && data.summary;

  return (
    <div className="cal-section">
      <div className="cal-toolbar">
        <select
          className="cal-select"
          value={groupKey}
          onChange={(event) => setGroupKey(event.target.value)}
          aria-label="Batch group"
        >
          {groups.length === 0 && <option value="">No batch groups</option>}
          {TRACK_ORDER.map((track) => {
            const trackGroups = groups.filter((candidate) => candidate.track === track);
            if (trackGroups.length === 0) return null;
            return (
              <optgroup key={track} label={TRACK_LABELS[track]}>
                {trackGroups.map((candidate) => (
                  <option key={candidate.key} value={candidate.key}>
                    {candidate.label} ({candidate.batchCount})
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>

        <div className="cal-month-nav">
          <button type="button" onClick={() => setMonth((mm) => shiftMonth(mm, -1))} aria-label="Previous month">
            <i className="bi bi-chevron-left" />
          </button>
          <span className="cal-month-label">{month}</span>
          <button type="button" onClick={() => setMonth((mm) => shiftMonth(mm, 1))} aria-label="Next month">
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      </div>

      <div className="cal-modefilter" role="group" aria-label="Filter by class type">
        <span className="cal-modefilter-label">Type</span>
        {MODES.map((mdVal) => {
          const active = modes.includes(mdVal);
          return (
            <button
              key={mdVal}
              type="button"
              className={`cal-modechip cal-modechip-${mdVal.toLowerCase().replace(/\s+/g, '-')}${active ? ' active' : ''}`}
              onClick={() => toggleMode(mdVal)}
              aria-pressed={active}
            >
              {mdVal}
            </button>
          );
        })}
        {modes.length > 0 && (
          <button type="button" className="cal-modechip-clear" onClick={() => setModes([])}>
            Clear
          </button>
        )}
      </div>

      {s && (
        <div className="cal-summary">
          <span className="cal-summary-stat">{s.flagged} flagged</span>
          <span className="cal-summary-stat">{s.missing_exam} missing exam</span>
          <span className="cal-summary-stat">{s.missing_live} missing live</span>
          <span className="cal-summary-stat">{s.empty} empty</span>
          {/* Counted apart from `empty`: these are unlinked in the hundreds and
              would otherwise hide the few genuinely broken schedules. */}
          {s.revision_live_unlinked > 0 && (
            <span className="cal-summary-stat cal-summary-stat-revision">
              {s.revision_live_unlinked} revision live unlinked
            </span>
          )}
          <span className="cal-summary-total">{s.total} total</span>
        </div>
      )}

      {error && <div className="cal-error">{error}</div>}

      {loading ? (
        <Skeleton count={6} height={90} />
      ) : data ? (
        <MonthGrid year={y} month={m} days={data.days} onSelectDay={(d) => setSelectedDay(d)} />
      ) : (
        !error && <div className="cal-empty">Select a batch group to view its schedule.</div>
      )}

      {selectedDay && data && (
        <DayDetail
          date={selectedDay}
          isoDate={selectedDay}
          month={month}
          courses={selectedGroup?.courses || []}
          batchIds={batchIds}
          groupLabel={selectedGroup?.label}
          items={data.days[selectedDay] || []}
          onClose={() => setSelectedDay(null)}
          onHealed={() => loadCalendar()}
          onScheduleMoved={() => {
            setSelectedDay(null);
            loadCalendar();
          }}
        />
      )}
    </div>
  );
};

export default CalendarSection;
